import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Check, ChevronRight, ListTodo, Plus, X, User, Search } from "lucide-react";
import { STORAGE_KEYS } from "@/utils/calendarConstants";
import { getLocalDateStr, getWeekNumber, loadData } from "@/utils/calendarUtils";
import { runTodoRollover } from "@/utils/todoRollover";

import { userStorage } from '../../utils/userStorage';
const API = process.env.REACT_APP_BACKEND_URL;

export default function TodosView({ onTransferToCalendar, onBulkTransferToWeek, appointments, onToggleAppointmentDone, initialTab }) {
  const [activeTab, setActiveTab] = useState(initialTab || 'daily');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [showBulkTransferModal, setShowBulkTransferModal] = useState(false);
  const [bulkTransferItems, setBulkTransferItems] = useState([]);
  const [currentBulkIndex, setCurrentBulkIndex] = useState(0);
  const [bulkTransferData, setBulkTransferData] = useState({});
  const [transferTarget, setTransferTarget] = useState(null);
  const [todoToTransfer, setTodoToTransfer] = useState(null);
  // Sticky-Header Schlank-Modus beim Scrollen
  const [headerCompact, setHeaderCompact] = useState(false);
  // Zwei-Stufen-Transfer: Zwischen-Auswahl (Tag / KW / Monat) wenn nicht 'daily'
  const [transferStage, setTransferStage] = useState('main'); // 'main' | 'detail'
  const [pendingTransferTarget, setPendingTransferTarget] = useState(null); // 'daily' | 'weekly' | 'monthly' | 'open'
  const [todoToEdit, setTodoToEdit] = useState(null);
  const [todoToCalendar, setTodoToCalendar] = useState(null);
  const [newTodo, setNewTodo] = useState({ text: '', priority: 5, dueDate: '', note: '' });
  
  // Contact search state for edit modal
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [contactSearchOpen, setContactSearchOpen] = useState(false);
  const contactSearchRef = useRef(null);
  const [addContactSearch, setAddContactSearch] = useState('');
  const [addContactResults, setAddContactResults] = useState([]);
  const [addContactSearchOpen, setAddContactSearchOpen] = useState(false);
  const addContactSearchRef = useRef(null);
  
  // Load todos from localStorage
  const [dailyTodos, setDailyTodos] = useState(() => {
    const today = getLocalDateStr();
    const stored = loadData(STORAGE_KEYS.dailyTodos, {});
    return stored[today] || [];
  });
  const [weeklyTodos, setWeeklyTodos] = useState(() => {
    // Get current week key (year-weekNumber)
    const now = new Date();
    const weekKey = `${now.getFullYear()}-W${getWeekNumber(now)}`;
    const stored = loadData(STORAGE_KEYS.weeklyTodos, {});
    return stored[weekKey] || [];
  });
  const [monthlyTodos, setMonthlyTodos] = useState(() => {
    // Get current month key (year-month)
    const now = new Date();
    const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const stored = loadData(STORAGE_KEYS.monthlyTodos, {});
    // Support both old array format and new object format
    if (Array.isArray(stored)) {
      // Migrate old format to new format
      const migrated = {};
      migrated[monthKey] = stored;
      userStorage.setItem(STORAGE_KEYS.monthlyTodos, JSON.stringify(migrated));
      return stored;
    }
    return stored[monthKey] || [];
  });
  const [openTodos, setOpenTodos] = useState(() => loadData(STORAGE_KEYS.backlogTodos, []));

  // Helper to get week number
  function getWeekNumber(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  }

  // Helper to get all weeks in current month with date ranges
  function getWeeksInCurrentMonth() {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const weeks = [];
    let currentDate = new Date(firstDay);
    
    while (currentDate <= lastDay) {
      const weekNum = getWeekNumber(currentDate);
      
      // Find the start of this week (Monday)
      const weekStart = new Date(currentDate);
      const dayOfWeek = weekStart.getDay() || 7;
      weekStart.setDate(weekStart.getDate() - dayOfWeek + 1);
      
      // Find the end of this week (Sunday)
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      
      // Only add if not already in list
      if (!weeks.find(w => w.weekNum === weekNum)) {
        weeks.push({
          weekNum,
          startDate: getLocalDateStr(weekStart),
          endDate: getLocalDateStr(weekEnd),
          startDay: weekStart.getDate(),
          startMonth: weekStart.getMonth(),
          endDay: weekEnd.getDate(),
          endMonth: weekEnd.getMonth()
        });
      }
      
      // Move to next week
      currentDate.setDate(currentDate.getDate() + 7 - (currentDate.getDay() || 7) + 1);
    }
    
    return weeks;
  }

  // Helper to get current week number
  const currentWeekNum = getWeekNumber(new Date());

  // Save to localStorage
  useEffect(() => {
    const today = getLocalDateStr();
    const stored = loadData(STORAGE_KEYS.dailyTodos, {});
    stored[today] = dailyTodos;
    userStorage.setItem(STORAGE_KEYS.dailyTodos, JSON.stringify(stored));
  }, [dailyTodos]);

  useEffect(() => {
    const now = new Date();
    const weekKey = `${now.getFullYear()}-W${getWeekNumber(now)}`;
    const stored = loadData(STORAGE_KEYS.weeklyTodos, {});
    stored[weekKey] = weeklyTodos;
    userStorage.setItem(STORAGE_KEYS.weeklyTodos, JSON.stringify(stored));
  }, [weeklyTodos]);

  useEffect(() => {
    const now = new Date();
    const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const stored = loadData(STORAGE_KEYS.monthlyTodos, {});
    // Handle migration from array to object format
    if (Array.isArray(stored)) {
      const migrated = {};
      migrated[monthKey] = monthlyTodos;
      userStorage.setItem(STORAGE_KEYS.monthlyTodos, JSON.stringify(migrated));
    } else {
      stored[monthKey] = monthlyTodos;
      userStorage.setItem(STORAGE_KEYS.monthlyTodos, JSON.stringify(stored));
    }
  }, [monthlyTodos]);

  useEffect(() => {
    userStorage.setItem(STORAGE_KEYS.backlogTodos, JSON.stringify(openTodos));
  }, [openTodos]);

  // Tag/Woche/Monat/Jahr-Rollover.
  // Läuft beim Mount + alle 60s + bei visibilitychange (PWA wieder aktiv).
  // Nutzt eine ref um den letzten Lauf-Tag zu tracken — falls die App über
  // Mitternacht offen bleibt, wird der Rollover automatisch ausgeführt.
  const lastRolloverDayRef = useRef(null);
  const applyRollover = useCallback(() => {
    const result = runTodoRollover(new Date());
    setDailyTodos(result.dailyToday);
    setWeeklyTodos(result.weeklyCurrent);
    if (result.monthlyChanged) setMonthlyTodos(result.monthlyCurrent);
    if (result.yearlyChanged) setOpenTodos(result.yearly);
    lastRolloverDayRef.current = result.today;
  }, []);

  useEffect(() => {
    // 1) Sofortiger Lauf beim Mount
    applyRollover();

    // 2) Periodischer Tageswechsel-Check (alle 60s)
    const intervalId = setInterval(() => {
      const today = getLocalDateStr(new Date());
      if (lastRolloverDayRef.current !== today) {
        applyRollover();
      }
    }, 60 * 1000);

    // 3) Wenn Tab/PWA wieder sichtbar wird → Rollover prüfen
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        const today = getLocalDateStr(new Date());
        if (lastRolloverDayRef.current !== today) {
          applyRollover();
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);

    // 4) Rollover-Event vom Parent (ZielplanungsKalender) – State neu laden
    const onParentRollover = () => applyRollover();
    window.addEventListener('burningteam-todo-rollover', onParentRollover);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
      window.removeEventListener('burningteam-todo-rollover', onParentRollover);
    };
  }, [applyRollover]);

  // Listen for transfer-complete event from WeekView/MonthView/YearView
  // The todo is only deleted AFTER the AppointmentModal is saved (not on cancel)
  useEffect(() => {
    const handleTransferComplete = (e) => {
      const { id, fromTab } = e.detail || {};
      if (!id) return;
      if (fromTab === 'daily') {
        const today = getLocalDateStr();
        const stored = loadData(STORAGE_KEYS.dailyTodos, {});
        stored[today] = (stored[today] || []).filter(t => t.id !== id);
        userStorage.setItem(STORAGE_KEYS.dailyTodos, JSON.stringify(stored));
        setDailyTodos(prev => prev.filter(t => t.id !== id));
      } else if (fromTab === 'weekly') {
        const storedW = loadData(STORAGE_KEYS.weeklyTodos, {});
        Object.keys(storedW).forEach(k => { storedW[k] = storedW[k].filter(t => t.id !== id); });
        userStorage.setItem(STORAGE_KEYS.weeklyTodos, JSON.stringify(storedW));
        setWeeklyTodos(prev => prev.filter(t => t.id !== id));
      } else if (fromTab === 'monthly') {
        const storedM = loadData(STORAGE_KEYS.monthlyTodos, {});
        Object.keys(storedM).forEach(k => { storedM[k] = storedM[k].filter(t => t.id !== id); });
        userStorage.setItem(STORAGE_KEYS.monthlyTodos, JSON.stringify(storedM));
        setMonthlyTodos(prev => prev.filter(t => t.id !== id));
      } else if (fromTab === 'open') {
        const storedO = loadData(STORAGE_KEYS.backlogTodos, []);
        const filteredO = storedO.filter(t => t.id !== id);
        userStorage.setItem(STORAGE_KEYS.backlogTodos, JSON.stringify(filteredO));
        setOpenTodos(prev => prev.filter(t => t.id !== id));
      }
    };
    window.addEventListener('burningteam-transfer-complete', handleTransferComplete);
    return () => window.removeEventListener('burningteam-transfer-complete', handleTransferComplete);
  }, []);

  const handleAddTodo = async () => {
    if (!newTodo.text.trim()) return;
    
    const todoId = Date.now();
    const taskId = newTodo.contactRef ? `task-${todoId}` : null;
    const todo = {
      id: todoId,
      text: newTodo.text,
      priority: newTodo.priority,
      dueDate: newTodo.dueDate || null,
      note: newTodo.note || null,
      done: false,
      createdAt: new Date().toISOString(),
      contactRef: newTodo.contactRef ? { ...newTodo.contactRef, taskId } : undefined,
    };

    // Get today's weekday (0=Mo, 6=So)
    const now = new Date();
    const todayWeekday = now.getDay() === 0 ? 6 : now.getDay() - 1;
    const currentWeekNum = getWeekNumber(now);
    const currentMonth = now.getMonth();

    switch (activeTab) {
      case 'daily':
        setDailyTodos(prev => [...prev, todo].sort((a, b) => b.priority - a.priority));
        break;
      case 'weekly':
        // If today's weekday is selected, redirect to Heute tab
        if (newTodo.weekday === todayWeekday) {
          setDailyTodos(prev => [...prev, todo].sort((a, b) => b.priority - a.priority));
          setActiveTab('daily');
        } else {
          const weeklyTodo = newTodo.weekday !== undefined ? { ...todo, weekday: newTodo.weekday } : todo;
          setWeeklyTodos(prev => [...prev, weeklyTodo].sort((a, b) => b.priority - a.priority));
        }
        break;
      case 'monthly':
        // If current KW is selected, redirect to Woche tab "Diese Woche"
        if (newTodo.weekNum === currentWeekNum) {
          setWeeklyTodos(prev => [...prev, todo].sort((a, b) => b.priority - a.priority));
          setActiveTab('weekly');
        } else {
          const monthlyTodo = newTodo.weekNum !== undefined ? { ...todo, weekNum: newTodo.weekNum } : todo;
          setMonthlyTodos(prev => [...prev, monthlyTodo].sort((a, b) => b.priority - a.priority));
        }
        break;
      case 'open':
        // If current month is selected, redirect to Monat tab "Diesen Monat"
        if (newTodo.monthIndex === currentMonth) {
          setMonthlyTodos(prev => [...prev, todo].sort((a, b) => b.priority - a.priority));
          setActiveTab('monthly');
        } else {
          const openTodo = newTodo.monthIndex !== undefined ? { ...todo, monthIndex: newTodo.monthIndex } : todo;
          setOpenTodos(prev => [...prev, openTodo].sort((a, b) => b.priority - a.priority));
        }
        break;
      default:
        break;
    }

    // Kontakt-Aufgabe erstellen falls verknüpft
    if (newTodo.contactRef?.id && taskId) {
      const contactTask = { id: taskId, text: todo.text, done: false, todoRef: todoId, dueDate: todo.dueDate || null, createdAt: todo.createdAt };
      try {
        if (newTodo.contactRef.typ === 'ich') {
          await axios.post(`${API}/api/auth/me/tasks`, contactTask);
        } else {
          await axios.post(`${API}/api/teampartner/${newTodo.contactRef.id}/tasks`, contactTask);
        }
      } catch { /* ignore */ }
    }
    
    setNewTodo({ text: '', priority: 5, dueDate: '', note: '', weekday: undefined, weekNum: undefined, monthIndex: undefined });
    setAddContactSearch('');
    setAddContactResults([]);
    setShowAddModal(false);
  };

  // Function to add a todo to weekly list (used by transfer modal)
  const addToWeeklyTodos = (todo) => {
    setWeeklyTodos(prev => [...prev, todo].sort((a, b) => b.priority - a.priority));
  };

  // Expose addToWeeklyTodos globally for YearView transfer modal
  useEffect(() => {
    window.addToWeeklyTodos = addToWeeklyTodos;
    return () => {
      delete window.addToWeeklyTodos;
    };
  }, []);

  // Live Kontakt-Suche für das "Neue Aufgabe" Modal
  useEffect(() => {
    if (!showAddModal || !addContactSearch.trim() || newTodo.contactRef) { if (!newTodo.contactRef) setAddContactResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/api/teampartner?typ=`);
        const q = addContactSearch.toLowerCase();
        setAddContactResults((res.data || []).filter(c => c.name?.toLowerCase().includes(q)).slice(0, 6));
      } catch { setAddContactResults([]); }
    }, 200);
    return () => clearTimeout(timer);
  }, [addContactSearch, showAddModal, newTodo.contactRef]);

  // Listen for contact-sync events (when Kunden.js updates a contact task and syncs to localStorage)
  useEffect(() => {    const handleContactSync = () => {
      const today = getLocalDateStr();
      const storedDaily = loadData(STORAGE_KEYS.dailyTodos, {});
      setDailyTodos(storedDaily[today] || []);

      const now = new Date();
      const weekKey = `${now.getFullYear()}-W${getWeekNumber(now)}`;
      const storedWeekly = loadData(STORAGE_KEYS.weeklyTodos, {});
      setWeeklyTodos(storedWeekly[weekKey] || []);

      const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const storedMonthly = loadData(STORAGE_KEYS.monthlyTodos, {});
      setMonthlyTodos(Array.isArray(storedMonthly) ? storedMonthly : (storedMonthly[monthKey] || []));

      setOpenTodos(loadData(STORAGE_KEYS.backlogTodos, []));
    };
    window.addEventListener('burningteam-todos-sync', handleContactSync);
    return () => window.removeEventListener('burningteam-todos-sync', handleContactSync);
  }, []);

  const toggleTodo = (tab, id, todo = null) => {
    const updateFn = (todos) => todos.map(t => t.id === id ? { ...t, done: !t.done } : t);
    switch (tab) {
      case 'daily': setDailyTodos(updateFn); break;
      case 'weekly': setWeeklyTodos(updateFn); break;
      case 'monthly': setMonthlyTodos(updateFn); break;
      case 'open': setOpenTodos(updateFn); break;
      default: break;
    }
    // Bidirectional sync: SET done state explicitly (not toggle) to avoid double-toggle bugs
    if (todo?.contactRef?.id && todo?.contactRef?.taskId) {
      const newDoneState = !todo.done; // the NEW state after toggle
      syncContactTask(todo, newDoneState);
    }
  };

  // Sync contact task when a linked todo is toggled — sets done state explicitly
  const syncContactTask = useCallback(async (todo, newDoneState) => {
    const ref = todo?.contactRef;
    if (!ref?.id || !ref?.taskId) return;
    try {
      if (ref.typ === 'ich') {
        await axios.patch(`${API}/api/auth/me/tasks/${ref.taskId}`, { done: newDoneState });
      } else {
        await axios.patch(`${API}/api/teampartner/${ref.id}/tasks/${ref.taskId}`, { done: newDoneState });
      }
    } catch (e) {
      console.error('Kontakt-Sync fehlgeschlagen:', e);
    }
  }, []);

  const deleteTodo = async (tab, id) => {
    // Find todo before deleting to get contactRef for bidirectional sync
    let todoToDelete = null;
    switch (tab) {
      case 'daily': todoToDelete = dailyTodos.find(t => t.id === id); break;
      case 'weekly': todoToDelete = weeklyTodos.find(t => t.id === id); break;
      case 'monthly': todoToDelete = monthlyTodos.find(t => t.id === id); break;
      case 'open': todoToDelete = openTodos.find(t => t.id === id); break;
      default: break;
    }
    const filterFn = (todos) => todos.filter(t => t.id !== id);
    switch (tab) {
      case 'daily': setDailyTodos(filterFn); break;
      case 'weekly': setWeeklyTodos(filterFn); break;
      case 'monthly': setMonthlyTodos(filterFn); break;
      case 'open': setOpenTodos(filterFn); break;
      default: break;
    }
    // Bidirektionale Sync: Verknüpfte Kontakt-Aufgabe ebenfalls löschen
    if (todoToDelete?.contactRef?.id && todoToDelete?.contactRef?.taskId) {
      const ref = todoToDelete.contactRef;
      try {
        if (ref.typ === 'ich') {
          await axios.delete(`${API}/api/auth/me/tasks/${ref.taskId}`);
        } else {
          await axios.delete(`${API}/api/teampartner/${ref.id}/tasks/${ref.taskId}`);
        }
      } catch { /* Sync-Fehler ignorieren */ }
    }
  };

  // Open edit modal when clicking on a todo
  const openEditModal = (todo) => {
    setTodoToEdit({ ...todo, tab: activeTab });
    setContactSearch(todo.contactRef?.name || '');
    setContactResults([]);
    setContactSearchOpen(false);
    setShowEditModal(true);
  };

  // Live search for contacts
  useEffect(() => {
    if (!showEditModal) return;
    if (!contactSearch.trim()) { setContactResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/api/teampartner?typ=`);
        const all = res.data || [];
        const q = contactSearch.toLowerCase();
        setContactResults(all.filter(c => c.name?.toLowerCase().includes(q)).slice(0, 8));
      } catch { setContactResults([]); }
    }, 200);
    return () => clearTimeout(timer);
  }, [contactSearch, showEditModal]);

  // Save edited todo
  const saveEditedTodo = async () => {
    if (!todoToEdit) return;

    const { tab, ...updatedTodo } = todoToEdit;

    // If a new contact was linked (no taskId yet), create a contact task
    if (updatedTodo.contactRef?.id && !updatedTodo.contactRef?.taskId) {
      try {
        const contactTaskId = `task-${updatedTodo.id}`;
        const newTask = {
          id: contactTaskId,
          text: updatedTodo.text,
          done: updatedTodo.done || false,
          todoRef: updatedTodo.id,
          dueDate: updatedTodo.dueDate || null,
          createdAt: new Date().toISOString(),
        };
        if (updatedTodo.contactRef.typ === 'ich') {
          await axios.post(`${API}/api/auth/me/tasks`, newTask);
        } else {
          await axios.post(`${API}/api/teampartner/${updatedTodo.contactRef.id}/tasks`, newTask);
        }
        updatedTodo.contactRef = { ...updatedTodo.contactRef, taskId: contactTaskId };
      } catch (e) { console.error('Kontaktaufgabe konnte nicht erstellt werden:', e); }
    }

    const updateFn = (todos) => todos
      .map(t => t.id === updatedTodo.id ? updatedTodo : t)
      .sort((a, b) => b.priority - a.priority);

    switch (tab) {
      case 'daily': setDailyTodos(updateFn); break;
      case 'weekly': setWeeklyTodos(updateFn); break;
      case 'monthly': setMonthlyTodos(updateFn); break;
      case 'open': setOpenTodos(updateFn); break;
      default: break;
    }

    setShowEditModal(false);
    setTodoToEdit(null);
    setContactSearch('');
  };

  // Delete from edit modal
  const deleteFromEditModal = () => {
    if (!todoToEdit) return;
    deleteTodo(todoToEdit.tab, todoToEdit.id);
    setShowEditModal(false);
    setTodoToEdit(null);
  };

  // Open calendar transfer modal
  const openCalendarTransfer = () => {
    if (!todoToTransfer) return;
    
    // Heute und Woche: Direkt zur Wochenansicht
    if (todoToTransfer.fromTab === 'daily' || todoToTransfer.fromTab === 'weekly') {
      // Direkt zur Wochenansicht transferieren
      transferToCalendarDirect(todoToTransfer, 'week');
      setShowTransferModal(false);
      return;
    }
    
    // Monat: Abfrage nur zwischen Monat und Woche
    if (todoToTransfer.fromTab === 'monthly') {
      setTodoToCalendar(todoToTransfer);
      setShowTransferModal(false);
      setShowCalendarModal(true);
      return;
    }
    
    // Offen: Alle Optionen zeigen
    setTodoToCalendar(todoToTransfer);
    setShowTransferModal(false);
    setShowCalendarModal(true);
  };

  // Hilfsfunktion: Todo lokal sofort aus dem passenden Storage entfernen.
  // Wird beim Transfer-Klick aufgerufen – der User erwartet, dass das Todo
  // sofort aus der Liste verschwindet (nicht erst nach Kalender-Save).
  const removeTodoFromStorage = (todo) => {
    if (!todo || !todo.id || !todo.fromTab) return;
    const { id, fromTab } = todo;
    if (fromTab === 'daily') {
      const today = getLocalDateStr();
      const stored = loadData(STORAGE_KEYS.dailyTodos, {});
      stored[today] = (stored[today] || []).filter(t => t.id !== id);
      userStorage.setItem(STORAGE_KEYS.dailyTodos, JSON.stringify(stored));
      setDailyTodos(prev => prev.filter(t => t.id !== id));
    } else if (fromTab === 'weekly') {
      const storedW = loadData(STORAGE_KEYS.weeklyTodos, {});
      Object.keys(storedW).forEach(k => { storedW[k] = (storedW[k] || []).filter(t => t.id !== id); });
      userStorage.setItem(STORAGE_KEYS.weeklyTodos, JSON.stringify(storedW));
      setWeeklyTodos(prev => prev.filter(t => t.id !== id));
    } else if (fromTab === 'monthly') {
      const storedM = loadData(STORAGE_KEYS.monthlyTodos, {});
      Object.keys(storedM).forEach(k => { storedM[k] = (storedM[k] || []).filter(t => t.id !== id); });
      userStorage.setItem(STORAGE_KEYS.monthlyTodos, JSON.stringify(storedM));
      setMonthlyTodos(prev => prev.filter(t => t.id !== id));
    } else if (fromTab === 'open') {
      const storedO = loadData(STORAGE_KEYS.backlogTodos, []);
      const filteredO = storedO.filter(t => t.id !== id);
      userStorage.setItem(STORAGE_KEYS.backlogTodos, JSON.stringify(filteredO));
      setOpenTodos(prev => prev.filter(t => t.id !== id));
    }
  };

  // Direct transfer to calendar (bypasses modal)
  const transferToCalendarDirect = (todo, calendarType) => {
    if (!todo || !onTransferToCalendar) return;
    onTransferToCalendar({
      type: calendarType,
      title: todo.text,
      note: todo.note || '',
      priority: todo.priority || 5,
      dueDate: todo.dueDate || null,
      fromTab: todo.fromTab,
      todoId: todo.id,
      contactRef: todo.contactRef || null,
    });
    // Todo sofort entfernen (User-Wunsch: Transfer → Todo weg, Kalendereintrag bleibt)
    removeTodoFromStorage(todo);
  };

  // Transfer to calendar view
  const transferToCalendar = (calendarType) => {
    if (!todoToCalendar || !onTransferToCalendar) return;
    onTransferToCalendar({
      type: calendarType,
      title: todoToCalendar.text,
      note: todoToCalendar.note || '',
      priority: todoToCalendar.priority || 5,
      dueDate: todoToCalendar.dueDate || null,
      fromTab: todoToCalendar.fromTab,
      todoId: todoToCalendar.id,
      contactRef: todoToCalendar.contactRef || null,
    });
    removeTodoFromStorage(todoToCalendar);
    setShowCalendarModal(false);
    setTodoToCalendar(null);
  };

  // Track appointments added during this bulk transfer session
  const [bulkAddedAppointments, setBulkAddedAppointments] = useState([]);

  // Helper to add one hour to a time string
  const addOneHour = (timeStr) => {
    const [h, m] = timeStr.split(':').map(Number);
    const newH = Math.min(h + 1, 23);
    return `${newH.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  // Find next available time slot (avoid short+short overlap)
  const findNextAvailableTime = (dateStr, preferredStartTime = '09:00', additionalAppointments = []) => {
    const allAppointments = [...(appointments || []), ...additionalAppointments];
    const dayAppointments = allAppointments.filter(apt => apt.date === dateStr && !apt.isMultiDay);
    if (dayAppointments.length === 0) return { startTime: preferredStartTime, endTime: addOneHour(preferredStartTime) };
    const sorted = dayAppointments.sort((a, b) => {
      const [aH, aM] = (a.startTime || '09:00').split(':').map(Number);
      const [bH, bM] = (b.startTime || '09:00').split(':').map(Number);
      return (aH * 60 + aM) - (bH * 60 + bM);
    });
    const slotDuration = 60;
    const [prefH, prefM] = preferredStartTime.split(':').map(Number);
    let candidateStart = prefH * 60 + prefM;
    for (let attempt = 0; attempt < 48; attempt++) {
      const candidateEnd = candidateStart + slotDuration;
      if (candidateEnd > 23 * 60) break;
      let hasOverlap = false;
      for (const apt of sorted) {
        const [aptSH, aptSM] = (apt.startTime || '09:00').split(':').map(Number);
        const [aptEH, aptEM] = (apt.endTime || '10:00').split(':').map(Number);
        if (candidateStart < (aptEH * 60 + aptEM) && candidateEnd > (aptSH * 60 + aptSM)) {
          candidateStart = aptEH * 60 + aptEM;
          hasOverlap = true;
          break;
        }
      }
      if (!hasOverlap) {
        const sH = Math.floor(candidateStart / 60), sM = candidateStart % 60;
        const ns = `${sH.toString().padStart(2, '0')}:${sM.toString().padStart(2, '0')}`;
        return { startTime: ns, endTime: addOneHour(ns) };
      }
    }
    return { startTime: '09:00', endTime: '10:00' };
  };

  // Open bulk transfer modal for "Alle Übertragen"
  const openBulkTransferModal = (tab = 'daily') => {
    let todosToTransfer = [];
    if (tab === 'daily') {
      todosToTransfer = dailyTodos.filter(t => !t.done);
    } else if (tab === 'weekly') {
      todosToTransfer = weeklyTodos.filter(t => !t.done);
    } else if (tab === 'monthly') {
      todosToTransfer = monthlyTodos.filter(t => !t.done);
    } else if (tab === 'open') {
      todosToTransfer = openTodos.filter(t => !t.done);
    }
    
    if (todosToTransfer.length === 0) return;
    
    // Store which tab we're transferring from
    setBulkTransferItems(todosToTransfer.map(t => ({ ...t, fromTab: tab })));
    setCurrentBulkIndex(0);
    setBulkAddedAppointments([]);
    
    const firstItem = todosToTransfer[0];
    const today = getLocalDateStr();
    
    // Find next available time for the first item
    const availableTime = findNextAvailableTime(today, '09:00', []);
    
    setBulkTransferData({
      title: firstItem.text || '',
      startTime: availableTime.startTime,
      endTime: availableTime.endTime,
      priority: firstItem.priority || 5,
      note: firstItem.note || '',
      isMultiDay: false,
      endDate: '',
      fromTab: tab
    });
    setShowBulkTransferModal(true);
  };

  // Save current bulk item and move to next
  const saveBulkTransferItem = () => {
    if (currentBulkIndex >= bulkTransferItems.length) return;
    
    const currentItem = bulkTransferItems[currentBulkIndex];
    const today = getLocalDateStr();
    
    // Create appointment for week view
    const weekAppointment = {
      id: Date.now(),
      title: bulkTransferData.title || currentItem.text || '',
      date: today,
      endDate: bulkTransferData.isMultiDay ? bulkTransferData.endDate : null,
      isMultiDay: bulkTransferData.isMultiDay,
      startTime: bulkTransferData.startTime || '09:00',
      endTime: bulkTransferData.endTime || '10:00',
      priority: bulkTransferData.priority || 5,
      note: bulkTransferData.note || '',
      color: null,
      transferredFrom: 'todo'
    };
    
    // Add to week appointments via callback
    if (onBulkTransferToWeek) {
      onBulkTransferToWeek(weekAppointment);
    }
    
    // Track this appointment for finding next available time
    const newBulkAdded = [...bulkAddedAppointments, weekAppointment];
    setBulkAddedAppointments(newBulkAdded);
    
    // Remove from the correct tab's todos
    const fromTab = currentItem.fromTab || bulkTransferData.fromTab || 'daily';
    if (fromTab === 'daily') {
      setDailyTodos(prev => prev.filter(t => t.id !== currentItem.id));
    } else if (fromTab === 'weekly') {
      setWeeklyTodos(prev => prev.filter(t => t.id !== currentItem.id));
    } else if (fromTab === 'monthly') {
      setMonthlyTodos(prev => prev.filter(t => t.id !== currentItem.id));
    } else if (fromTab === 'open') {
      setOpenTodos(prev => prev.filter(t => t.id !== currentItem.id));
    }
    
    // Move to next item with the updated appointments
    moveToNextBulkItem(newBulkAdded);
  };

  // Skip current item and move to next
  const skipBulkTransferItem = () => {
    moveToNextBulkItem(bulkAddedAppointments);
  };

  // Move to next item in bulk transfer
  const moveToNextBulkItem = (addedAppointments = []) => {
    const nextIndex = currentBulkIndex + 1;
    const today = getLocalDateStr();
    
    if (nextIndex >= bulkTransferItems.length) {
      // Done with all items
      setShowBulkTransferModal(false);
      setBulkTransferItems([]);
      setCurrentBulkIndex(0);
      setBulkTransferData({});
      setBulkAddedAppointments([]);
    } else {
      setCurrentBulkIndex(nextIndex);
      const nextItem = bulkTransferItems[nextIndex];
      
      // Find next available time considering already added appointments
      const nextAvailable = findNextAvailableTime(today, '09:00', addedAppointments);
      
      setBulkTransferData({
        title: nextItem.text || '',
        startTime: nextAvailable.startTime,
        endTime: nextAvailable.endTime,
        priority: nextItem.priority || 5,
        note: nextItem.note || '',
        isMultiDay: false,
        endDate: ''
      });
    }
  };

  const openTransferDialog = (fromTab, todo) => {
    setTodoToTransfer({ ...todo, fromTab });
    setShowTransferModal(true);
  };

  const executeTransfer = (targetTab, detail = {}) => {
    if (!todoToTransfer) return;

    const { fromTab, ...todo } = todoToTransfer;

    // Bewegungsrichtung bestimmen (daily=0, weekly=1, monthly=2, open/yearly=3)
    const order = { daily: 0, weekly: 1, monthly: 2, open: 3 };
    const fromLevel = order[fromTab];
    const toLevel = order[targetTab];
    // Nur einen Schritt nach oben erlaubt (daily→weekly OK, daily→monthly BLOCK)
    if (toLevel !== undefined && fromLevel !== undefined && toLevel > fromLevel + 1) {
      toast.error('Nur ein Schritt nach oben möglich.');
      return;
    }

    // Entferne stale Zeit-Zuordnungen (weekday/weekNum/monthIndex) aus Source
    const { weekday: _wd, weekNum: _kw, monthIndex: _mi, ...cleanTodo } = todo;

    // Remove from source
    if (fromTab === 'daily') {
      setDailyTodos(prev => prev.filter(t => t.id !== todo.id));
    } else if (fromTab === 'monthly') {
      setMonthlyTodos(prev => prev.filter(t => t.id !== todo.id));
    } else if (fromTab === 'open') {
      setOpenTodos(prev => prev.filter(t => t.id !== todo.id));
    } else if (fromTab === 'weekly') {
      setWeeklyTodos(prev => prev.filter(t => t.id !== todo.id));
    }

    // Add to target – mit oder ohne Detail-Zuordnung
    if (targetTab === 'daily') {
      // Heute: keine Detail-Zuordnung möglich (nur ein "Tag")
      setDailyTodos(prev => [...prev, { ...cleanTodo, done: false }].sort((a, b) => b.priority - a.priority));
    } else if (targetTab === 'weekly') {
      const assigned = detail.weekday !== undefined ? { weekday: detail.weekday } : {};
      setWeeklyTodos(prev => [...prev, { ...cleanTodo, ...assigned, done: false }].sort((a, b) => b.priority - a.priority));
    } else if (targetTab === 'monthly') {
      const assigned = detail.weekNum !== undefined ? { weekNum: detail.weekNum } : {};
      setMonthlyTodos(prev => [...prev, { ...cleanTodo, ...assigned, done: false }].sort((a, b) => b.priority - a.priority));
    } else if (targetTab === 'open') {
      const assigned = detail.monthIndex !== undefined ? { monthIndex: detail.monthIndex } : {};
      setOpenTodos(prev => [...prev, { ...cleanTodo, ...assigned, done: false }].sort((a, b) => b.priority - a.priority));
    }

    // Modal schließen und State zurücksetzen
    setShowTransferModal(false);
    setTodoToTransfer(null);
    setTransferStage('main');
    setPendingTransferTarget(null);
  };

  // Detail-Stage öffnen: fragt nach genauem Ziel (Tag/KW/Monat) oder "allgemein"
  const startTransferWithDetail = (targetTab) => {
    if (targetTab === 'daily') {
      // Heute hat keine Detail-Ebene → direkt übertragen
      executeTransfer('daily');
      return;
    }
    setPendingTransferTarget(targetTab);
    setTransferStage('detail');
  };

  // Change weekday of a weekly todo (Tag wechseln)
  const changeTodoWeekday = (newWeekday) => {
    if (!todoToTransfer) return;
    setWeeklyTodos(prev => prev.map(t => {
      if (t.id !== todoToTransfer.id) return t;
      if (newWeekday === undefined) {
        const { weekday, ...rest } = t;
        return rest;
      }
      return { ...t, weekday: newWeekday };
    }));
    setShowTransferModal(false);
    setTodoToTransfer(null);
  };

  // Change KW of a monthly todo (KW wechseln)
  const changeTodoKW = (newWeekNum) => {
    if (!todoToTransfer) return;
    setMonthlyTodos(prev => prev.map(t => {
      if (t.id !== todoToTransfer.id) return t;
      if (newWeekNum === undefined) {
        const { weekNum, ...rest } = t;
        return rest;
      }
      return { ...t, weekNum: newWeekNum };
    }));
    setShowTransferModal(false);
    setTodoToTransfer(null);
  };

  // Change month of an open/yearly todo (Monat wechseln)
  const changeTodoMonth = (newMonthIdx) => {
    if (!todoToTransfer) return;
    setOpenTodos(prev => prev.map(t => {
      if (t.id !== todoToTransfer.id) return t;
      if (newMonthIdx === undefined) {
        const { monthIndex, ...rest } = t;
        return rest;
      }
      return { ...t, monthIndex: newMonthIdx };
    }));
    setShowTransferModal(false);
    setTodoToTransfer(null);
  };

  const getPriorityColor = (priority) => {
    if (priority >= 8) return 'border-l-red-500 bg-red-50';
    if (priority >= 5) return 'border-l-amber-500 bg-amber-50';
    return 'border-l-green-500 bg-green-50';
  };

  const getPriorityBadge = (priority) => {
    if (priority >= 8) return 'bg-red-100 text-red-700';
    if (priority >= 5) return 'bg-amber-100 text-amber-700';
    return 'bg-green-100 text-green-700';
  };

  const formatDueDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };

  const tabs = [
    { id: 'daily', label: "Heute", count: dailyTodos.length },
    { id: 'weekly', label: "Woche", count: weeklyTodos.length },
    { id: 'monthly', label: "Monat", count: monthlyTodos.length },
    { id: 'open', label: "Dieses Jahr", count: openTodos.length }
  ];

  const WEEKDAY_NAMES = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  const getCurrentTodos = () => {
    switch (activeTab) {
      case 'daily': return dailyTodos;
      case 'weekly': 
        // Sort weekly todos by weekday (Mo-So), items without weekday at the end
        return [...weeklyTodos].sort((a, b) => {
          const aDay = a.weekday !== undefined ? a.weekday : 999;
          const bDay = b.weekday !== undefined ? b.weekday : 999;
          return aDay - bDay;
        });
      case 'monthly': return monthlyTodos;
      case 'open': return openTodos;
      default: return [];
    }
  };

  // Scroll-Listener: Header schlank machen beim Scrollen (> 80px)
  useEffect(() => {
    const handleScroll = () => {
      setHeaderCompact(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getWeekdayLabel = (weekday) => {
    if (weekday === undefined || weekday === null) return null;
    return WEEKDAY_NAMES[weekday];
  };

  // Group weekly todos by weekday for list view
  const getWeeklyTodosGroupedForList = () => {
    const grouped = { unassigned: [] };
    for (let i = 0; i < 7; i++) {
      grouped[i] = [];
    }
    
    weeklyTodos.forEach(todo => {
      if (todo.weekday === undefined || todo.weekday === null) {
        grouped.unassigned.push(todo);
      } else {
        grouped[todo.weekday].push(todo);
      }
    });
    
    return grouped;
  };

  // Get weeks in current month for grouping
  const getWeeksInMonthForTodos = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    
    const weeks = [];
    let currentDate = new Date(firstDayOfMonth);
    
    while (currentDate <= lastDayOfMonth) {
      const weekNum = getWeekNumber(currentDate);
      
      // Find the start of this week (Monday)
      const weekStart = new Date(currentDate);
      const dayOfWeek = weekStart.getDay() || 7;
      weekStart.setDate(weekStart.getDate() - dayOfWeek + 1);
      
      // Find the end of this week (Sunday)
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      
      // Only add if not already in list
      if (!weeks.find(w => w.weekNum === weekNum)) {
        const formatRange = () => {
          if (weekStart.getMonth() === weekEnd.getMonth()) {
            return `${weekStart.getDate()}.-${weekEnd.getDate()}. ${months[weekStart.getMonth()]}`;
          }
          return `${weekStart.getDate()}. ${months[weekStart.getMonth()]} - ${weekEnd.getDate()}. ${months[weekEnd.getMonth()]}`;
        };
        
        weeks.push({
          weekNum,
          dateRange: formatRange(),
          isCurrent: weekNum === getWeekNumber(now)
        });
      }
      
      // Move to next week
      currentDate.setDate(currentDate.getDate() + 7 - (currentDate.getDay() || 7) + 1);
    }
    
    return weeks;
  };

  // Group monthly todos by KW for list view
  const getMonthlyTodosGroupedForList = () => {
    const weeks = getWeeksInMonthForTodos();
    const grouped = { unassigned: [] };
    
    weeks.forEach(week => {
      grouped[week.weekNum] = [];
    });
    
    monthlyTodos.forEach(todo => {
      if (todo.weekNum === undefined || todo.weekNum === null) {
        grouped.unassigned.push(todo);
      } else if (grouped[todo.weekNum]) {
        grouped[todo.weekNum].push(todo);
      } else {
        grouped.unassigned.push(todo);
      }
    });
    
    return grouped;
  };

  // Group open (yearly) todos by month for list view
  const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
  
  const getOpenTodosGroupedByMonth = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const grouped = { unassigned: [] };
    
    for (let i = 0; i < 12; i++) {
      grouped[i] = [];
    }
    
    openTodos.forEach(todo => {
      if (todo.monthIndex === undefined || todo.monthIndex === null) {
        grouped.unassigned.push(todo);
      } else if (grouped[todo.monthIndex] !== undefined) {
        grouped[todo.monthIndex].push(todo);
      } else {
        grouped.unassigned.push(todo);
      }
    });
    
    return { grouped, currentMonth };
  };

  return (
    <div className="space-y-4">
      {/* Header with Add Button - Sticky unter ViewSelector (Jahr/Monat/Woche/Todos) */}
      <div
        className={`bg-white rounded-xl shadow-sm border border-slate-200 sticky top-[110px] z-10 transition-all duration-200 ${
          headerCompact ? 'p-2' : 'p-4'
        }`}
      >
        <div className={`flex items-center justify-between transition-all duration-200 ${headerCompact ? 'mb-2' : 'mb-4'}`}>
          <h2 className="text-lg font-bold text-slate-800">To-Do's</h2>
          <button
            onClick={() => setShowAddModal(true)}
            className={`flex items-center gap-1 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-all duration-200 ${
              headerCompact ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
            }`}
          >
            <Plus className={`transition-all duration-200 ${headerCompact ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />
            Hinzufügen
          </button>
        </div>

        {/* Tabs */}
        <div className={`flex gap-1 bg-slate-100 rounded-lg transition-all duration-200 ${headerCompact ? 'p-0.5' : 'p-1'}`}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-1.5 sm:px-3 rounded-md text-xs sm:text-sm font-medium transition-all ${
                headerCompact ? 'py-1' : 'py-2'
              } ${
                activeTab === tab.id
                  ? 'bg-white text-emerald-700 shadow-sm'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Todo List */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 min-h-[300px]">
        {activeTab === 'weekly' ? (
          /* Grouped view for Weekly tab */
          <div className="space-y-4">
            {(() => {
              const grouped = getWeeklyTodosGroupedForList();
              const now = new Date();
              const todayWeekday = now.getDay() === 0 ? 6 : now.getDay() - 1;
              
              // Define sections: "Diese Woche" first, then weekdays
              const sections = [
                { key: 'unassigned', name: 'Diese Woche', color: 'purple' },
                ...WEEKDAY_NAMES.map((name, idx) => ({ key: idx, name, color: idx === todayWeekday ? 'emerald' : 'slate' }))
              ];
              
              return sections.map(section => {
                const todos = grouped[section.key] || [];
                const isPastDay = typeof section.key === 'number' && section.key < todayWeekday;
                // Show all sections, skip empty past days
                if (todos.length === 0 && isPastDay) return null;
                
                return (
                  <div key={`weekly-${section.key}`}>
                    {/* Section Header */}
                    <div className={`flex items-center gap-2 mb-2 pb-1 border-b ${
                      section.color === 'emerald' ? 'border-emerald-200' : 
                      section.color === 'purple' ? 'border-purple-200' : 'border-slate-200'
                    }`}>
                      <span className={`text-xs font-bold uppercase tracking-wide ${
                        section.color === 'emerald' ? 'text-emerald-600' : 
                        section.color === 'purple' ? 'text-purple-600' : 
                        isPastDay ? 'text-slate-400' : 'text-slate-500'
                      }`}>
                        {section.name}
                      </span>
                      {section.key === todayWeekday && (
                        <span className="text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded-full">
                          Heute
                        </span>
                      )}
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                        section.color === 'emerald' ? 'bg-emerald-100 text-emerald-700' : 
                        section.color === 'purple' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {todos.filter(t => !t.done).length} offen
                      </span>
                    </div>
                    
                    {/* Todos in this section */}
                    <div className="space-y-1.5 ml-2">
                      {todos.map(todo => (
                        <div
                          key={todo.id}
                          className={`p-3 rounded-lg border-l-4 cursor-pointer hover:shadow-md transition-shadow ${getPriorityColor(todo.priority)} ${
                            todo.done ? 'opacity-60' : ''
                          }`}
                          onClick={() => openEditModal(todo)}
                        >
                          <div className="flex items-start gap-3">
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleTodo(activeTab, todo.id, todo); }}
                              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all mt-0.5 ${
                                todo.done
                                  ? 'bg-emerald-600 border-emerald-600 text-white'
                                  : 'border-slate-300 hover:border-emerald-500'
                              }`}
                            >
                              {todo.done && <Check className="w-4 h-4" />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className={`text-sm ${todo.done ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                                  {todo.text}
                                </p>
                              </div>
                              {todo.note && (
                                <p className="text-xs text-slate-500 mt-0.5 italic">
                                  {todo.note}
                                </p>
                              )}
                              {todo.dueDate && (
                                <p className="text-xs text-slate-500 mt-0.5">
                                  Fällig bis: {formatDueDate(todo.dueDate)}
                                </p>
                              )}
                              {todo.contactRef?.name && (
                                <p className="text-xs text-emerald-600 mt-0.5 flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {todo.contactRef.name}
                                </p>
                              )}

                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityBadge(todo.priority)}`}>
                                P{todo.priority}
                              </span>
                              <button
                                onClick={(e) => { e.stopPropagation(); deleteTodo(activeTab, todo.id); }}
                                className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          {!todo.done && (
                            <div className="mt-1 pt-1 border-t border-slate-200/50">
                              <button
                                onClick={(e) => { e.stopPropagation(); openTransferDialog(activeTab, todo); }}
                                className="ml-9 px-2 py-0.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded flex items-center gap-1 w-fit"
                              >
                                <ChevronRight className="w-3 h-3" />
                                Übertragen
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {/* Quick add inline */}
                      {!isPastDay && (
                        <div className="flex gap-1.5 mt-2">
                          <input
                            type="text"
                            enterKeyHint="done"
                            placeholder={`+ Aufgabe für ${section.name}...`}
                            className="flex-1 p-2 border border-slate-200 rounded-lg text-sm bg-white"
                            data-testid={`week-add-todo-${section.key}`}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && e.target.value.trim()) {
                                const inputText = e.target.value.trim();
                                const weekday = section.key === 'unassigned' ? undefined : section.key;
                                // Smart redirect for today
                                if (weekday === todayWeekday) {
                                  setDailyTodos(prev => [...prev, {
                                    id: Date.now(),
                                    text: inputText,
                                    priority: 5,
                                    done: false,
                                    createdAt: new Date().toISOString()
                                  }].sort((a, b) => b.priority - a.priority));
                                  setActiveTab('daily');
                                } else {
                                  const newItem = {
                                    id: Date.now(),
                                    text: inputText,
                                    priority: 5,
                                    done: false,
                                    createdAt: new Date().toISOString()
                                  };
                                  if (weekday !== undefined) newItem.weekday = weekday;
                                  setWeeklyTodos(prev => [...prev, newItem].sort((a, b) => b.priority - a.priority));
                                }
                                e.target.value = '';
                              }
                            }}
                          />
                        </div>
                      )}
                      {isPastDay && todos.length > 0 && (
                        <div className="text-[10px] text-slate-400 text-center py-1">Liegt in der Vergangenheit</div>
                      )}
                    </div>
                  </div>
                );
              }).filter(Boolean);
            })()}
          </div>
        ) : activeTab === 'monthly' ? (
          /* Grouped view for Monthly tab by KW */
          <div className="space-y-4">
            {(() => {
              const grouped = getMonthlyTodosGroupedForList();
              const weeks = getWeeksInMonthForTodos();
              
              // Define sections: "Diesen Monat" first, then KWs
              const sections = [
                { key: 'unassigned', name: 'Diesen Monat', dateRange: '', color: 'purple', isCurrent: false },
                ...weeks.map(week => ({ 
                  key: week.weekNum, 
                  name: `KW ${week.weekNum}`, 
                  dateRange: week.dateRange,
                  color: week.isCurrent ? 'emerald' : 'slate',
                  isCurrent: week.isCurrent
                }))
              ];
              
              return sections.map(section => {
                const todos = grouped[section.key] || [];
                const currentWeek = getWeekNumber(new Date());
                const isPastKW = typeof section.key === 'number' && section.key < currentWeek;
                // Show all sections, skip empty past KWs
                if (todos.length === 0 && isPastKW) return null;
                
                return (
                  <div key={`monthly-${section.key}`}>
                    {/* Section Header */}
                    <div className={`flex items-center gap-2 mb-2 pb-1 border-b ${
                      section.color === 'emerald' ? 'border-emerald-200' : 
                      section.color === 'purple' ? 'border-purple-200' : 'border-slate-200'
                    }`}>
                      <span className={`text-xs font-bold uppercase tracking-wide ${
                        section.color === 'emerald' ? 'text-emerald-600' : 
                        section.color === 'purple' ? 'text-purple-600' : 
                        isPastKW ? 'text-slate-400' : 'text-slate-500'
                      }`}>
                        {section.name}
                      </span>
                      {section.dateRange && (
                        <span className="text-[10px] text-slate-400">
                          ({section.dateRange})
                        </span>
                      )}
                      {section.isCurrent && (
                        <span className="text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded-full">
                          Aktuell
                        </span>
                      )}
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                        section.color === 'emerald' ? 'bg-emerald-100 text-emerald-700' : 
                        section.color === 'purple' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {todos.filter(t => !t.done).length} offen
                      </span>
                    </div>
                    
                    {/* Todos in this section */}
                    <div className="space-y-1.5 ml-2">
                      {todos.map(todo => (
                        <div
                          key={todo.id}
                          className={`p-3 rounded-lg border-l-4 cursor-pointer hover:shadow-md transition-shadow ${getPriorityColor(todo.priority)} ${
                            todo.done ? 'opacity-60' : ''
                          }`}
                          onClick={() => openEditModal(todo)}
                        >
                          <div className="flex items-start gap-3">
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleTodo(activeTab, todo.id, todo); }}
                              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all mt-0.5 ${
                                todo.done
                                  ? 'bg-emerald-600 border-emerald-600 text-white'
                                  : 'border-slate-300 hover:border-emerald-500'
                              }`}
                            >
                              {todo.done && <Check className="w-4 h-4" />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className={`text-sm ${todo.done ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                                  {todo.text}
                                </p>
                              </div>
                              {todo.note && (
                                <p className="text-xs text-slate-500 mt-0.5 italic">
                                  {todo.note}
                                </p>
                              )}
                              {todo.dueDate && (
                                <p className="text-xs text-slate-500 mt-0.5">
                                  Fällig bis: {formatDueDate(todo.dueDate)}
                                </p>
                              )}
                              {todo.contactRef?.name && (
                                <p className="text-xs text-emerald-600 mt-0.5 flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {todo.contactRef.name}
                                </p>
                              )}

                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityBadge(todo.priority)}`}>
                                P{todo.priority}
                              </span>
                              <button
                                onClick={(e) => { e.stopPropagation(); deleteTodo(activeTab, todo.id); }}
                                className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          {!todo.done && (
                            <div className="mt-1 pt-1 border-t border-slate-200/50">
                              <button
                                onClick={(e) => { e.stopPropagation(); openTransferDialog(activeTab, todo); }}
                                className="ml-9 px-2 py-0.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded flex items-center gap-1 w-fit"
                              >
                                <ChevronRight className="w-3 h-3" />
                                Übertragen
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {/* Quick add inline */}
                      {!isPastKW && (
                        <div className="flex gap-1.5 mt-2">
                          <input
                            type="text"
                            enterKeyHint="done"
                            placeholder={`+ Aufgabe für ${section.name}...`}
                            className="flex-1 p-2 border border-slate-200 rounded-lg text-sm bg-white"
                            data-testid={`month-add-todo-${section.key}`}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && e.target.value.trim()) {
                                const inputText = e.target.value.trim();
                                const weekNum = section.key === 'unassigned' ? undefined : section.key;
                                const cw = getWeekNumber(new Date());
                                // Smart redirect for current week
                                if (weekNum === cw) {
                                  setWeeklyTodos(prev => [...prev, {
                                    id: Date.now(),
                                    text: inputText,
                                    priority: 5,
                                    done: false,
                                    createdAt: new Date().toISOString()
                                  }].sort((a, b) => b.priority - a.priority));
                                  setActiveTab('weekly');
                                } else {
                                  const newItem = {
                                    id: Date.now(),
                                    text: inputText,
                                    priority: 5,
                                    done: false,
                                    createdAt: new Date().toISOString()
                                  };
                                  if (weekNum !== undefined) newItem.weekNum = weekNum;
                                  setMonthlyTodos(prev => [...prev, newItem].sort((a, b) => b.priority - a.priority));
                                }
                                e.target.value = '';
                              }
                            }}
                          />
                        </div>
                      )}
                      {isPastKW && todos.length > 0 && (
                        <div className="text-[10px] text-slate-400 text-center py-1">Liegt in der Vergangenheit</div>
                      )}
                    </div>
                  </div>
                );
              }).filter(Boolean);
            })()}
          </div>
        ) : activeTab === 'open' ? (
          /* Grouped view for Dieses Jahr tab by Month */
          <div className="space-y-4">
            {(() => {
              const { grouped, currentMonth } = getOpenTodosGroupedByMonth();
              const now = new Date();
              
              const sections = [
                { key: 'unassigned', name: 'Dieses Jahr/Backlog', color: 'purple', isCurrent: false, isPast: false },
                ...MONTH_NAMES_SHORT.map((name, idx) => ({ 
                  key: idx, 
                  name, 
                  color: idx === currentMonth ? 'emerald' : 'slate',
                  isCurrent: idx === currentMonth,
                  isPast: idx < currentMonth
                }))
              ];
              
              return sections.map(section => {
                const todos = grouped[section.key] || [];
                // Show future/current months even if empty, skip empty past months
                if (todos.length === 0 && section.isPast) return null;
                
                return (
                  <div key={`open-${section.key}`}>
                    {/* Section Header */}
                    <div className={`flex items-center gap-2 mb-2 pb-1 border-b ${
                      section.color === 'emerald' ? 'border-emerald-200' : 
                      section.color === 'purple' ? 'border-purple-200' : 'border-slate-200'
                    }`}>
                      <span className={`text-xs font-bold uppercase tracking-wide ${
                        section.color === 'emerald' ? 'text-emerald-600' : 
                        section.color === 'purple' ? 'text-purple-600' : 
                        section.isPast ? 'text-slate-400' : 'text-slate-500'
                      }`}>
                        {section.name}
                      </span>
                      {section.isCurrent && (
                        <span className="text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded-full">
                          Aktuell
                        </span>
                      )}
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                        section.color === 'emerald' ? 'bg-emerald-100 text-emerald-700' : 
                        section.color === 'purple' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {todos.filter(t => !t.done).length} offen
                      </span>
                    </div>
                    
                    {/* Todos in this section */}
                    <div className="space-y-1.5 ml-2">
                      {todos.map(todo => (
                        <div
                          key={todo.id}
                          className={`p-3 rounded-lg border-l-4 cursor-pointer hover:shadow-md transition-shadow ${getPriorityColor(todo.priority)} ${
                            todo.done ? 'opacity-60' : ''
                          }`}
                          onClick={() => openEditModal(todo)}
                        >
                          <div className="flex items-start gap-3">
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleTodo(activeTab, todo.id, todo); }}
                              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all mt-0.5 ${
                                todo.done
                                  ? 'bg-emerald-600 border-emerald-600 text-white'
                                  : 'border-slate-300 hover:border-emerald-500'
                              }`}
                            >
                              {todo.done && <Check className="w-4 h-4" />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className={`text-sm ${todo.done ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                                  {todo.text}
                                </p>
                              </div>
                              {todo.note && (
                                <p className="text-xs text-slate-500 mt-0.5 italic">
                                  {todo.note}
                                </p>
                              )}
                              {todo.dueDate && (
                                <p className="text-xs text-slate-500 mt-0.5">
                                  Fällig bis: {formatDueDate(todo.dueDate)}
                                </p>
                              )}
                              {todo.contactRef?.name && (
                                <p className="text-xs text-emerald-600 mt-0.5 flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {todo.contactRef.name}
                                </p>
                              )}

                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityBadge(todo.priority)}`}>
                                P{todo.priority}
                              </span>
                              <button
                                onClick={(e) => { e.stopPropagation(); deleteTodo(activeTab, todo.id); }}
                                className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          {!todo.done && (
                            <div className="mt-1 pt-1 border-t border-slate-200/50">
                              <button
                                onClick={(e) => { e.stopPropagation(); openTransferDialog(activeTab, todo); }}
                                className="ml-9 px-2 py-0.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded flex items-center gap-1 w-fit"
                              >
                                <ChevronRight className="w-3 h-3" />
                                Übertragen
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {/* Quick add inline - only for current/future months */}
                      {!section.isPast && (
                        <div className="flex gap-1.5 mt-2">
                          <input
                            type="text"
                            enterKeyHint="done"
                            placeholder={`+ Aufgabe für ${section.name}...`}
                            className="flex-1 p-2 border border-slate-200 rounded-lg text-sm bg-white"
                            data-testid={`year-add-todo-${section.key}`}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && e.target.value.trim()) {
                                const inputText = e.target.value.trim();
                                const monthIdx = section.key === 'unassigned' ? undefined : section.key;
                                if (monthIdx === currentMonth) {
                                  setMonthlyTodos(prev => [...prev, {
                                    id: Date.now(),
                                    text: inputText,
                                    priority: 5,
                                    done: false,
                                    createdAt: new Date().toISOString()
                                  }].sort((a, b) => b.priority - a.priority));
                                  setActiveTab('monthly');
                                } else {
                                  const newItem = {
                                    id: Date.now(),
                                    text: inputText,
                                    priority: 5,
                                    done: false,
                                    createdAt: new Date().toISOString()
                                  };
                                  if (monthIdx !== undefined) newItem.monthIndex = monthIdx;
                                  setOpenTodos(prev => [...prev, newItem].sort((a, b) => b.priority - a.priority));
                                }
                                e.target.value = '';
                              }
                            }}
                          />
                        </div>
                      )}
                      {section.isPast && todos.length > 0 && (
                        <div className="text-[10px] text-slate-400 text-center py-1">Liegt in der Vergangenheit</div>
                      )}
                    </div>
                  </div>
                );
              }).filter(Boolean);
            })()}
          </div>
        ) : getCurrentTodos().length === 0 ? (
          (() => {
            const todayStr = getLocalDateStr();
            const todayAppointments = (appointments || []).filter(apt =>
              apt.date === todayStr && !apt.isMultiDay && !apt.isHabitReminder
            );
            return (
              <div>
                <div className="text-center py-8">
                  <ListTodo className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Keine Aufgaben</p>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="mt-3 text-emerald-600 text-sm font-medium hover:underline"
                  >
                    + Erste Aufgabe hinzufügen
                  </button>
                </div>
                {todayAppointments.length > 0 && (
                  <div className="mt-2 pt-4 border-t border-slate-200">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Termine</p>
                    <div className="space-y-2">
                      {todayAppointments.map(apt => (
                        <div key={apt.id} className={`flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white ${apt.done ? 'opacity-60' : ''}`}>
                          <button
                            onClick={() => onToggleAppointmentDone && onToggleAppointmentDone(apt.id)}
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                              apt.done ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-slate-300 hover:border-emerald-500'
                            }`}
                          >
                            {apt.done && <Check className="w-4 h-4" />}
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${apt.done ? 'line-through text-slate-400' : 'text-slate-700'}`}>{apt.title}</p>
                            <p className="text-xs text-slate-400">{apt.startTime} – {apt.endTime}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()
        ) : (
          (() => {
            const todayStr = getLocalDateStr();
            const todayAppointments = (appointments || []).filter(apt =>
              apt.date === todayStr && !apt.isMultiDay && !apt.isHabitReminder
            );
            return (
              <div>
                {todayAppointments.length > 0 && (
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Aufgaben</p>
                )}
                <div className="space-y-2">
                  {getCurrentTodos().map(todo => (
                    <div
                      key={todo.id}
                      className={`p-3 rounded-lg border-l-4 cursor-pointer hover:shadow-md transition-shadow ${getPriorityColor(todo.priority)} ${
                        todo.done ? 'opacity-60' : ''
                      }`}
                      onClick={() => openEditModal(todo)}
                    >
                      {/* Main row: Checkbox + Text + Priority */}
                      <div className="flex items-start gap-3">
                        {/* Checkbox */}
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleTodo(activeTab, todo.id, todo); }}
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all mt-0.5 ${
                            todo.done
                              ? 'bg-emerald-600 border-emerald-600 text-white'
                              : 'border-slate-300 hover:border-emerald-500'
                          }`}
                        >
                          {todo.done && <Check className="w-4 h-4" />}
                        </button>

                        {/* Text - full width */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`text-sm ${todo.done ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                              {todo.text}
                            </p>
                          </div>
                          {todo.note && (
                            <p className="text-xs text-slate-500 mt-0.5 italic">
                              {todo.note}
                            </p>
                          )}
                          {todo.dueDate && (
                            <p className="text-xs text-slate-500 mt-0.5">
                              Fällig bis: {formatDueDate(todo.dueDate)}
                            </p>
                          )}
                          {todo.contactRef?.name && (
                            <p className="text-xs text-emerald-600 mt-0.5 flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {todo.contactRef.name}
                            </p>
                          )}

                        </div>

                        {/* Priority Badge + Delete - top right */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityBadge(todo.priority)}`}>
                            P{todo.priority}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteTodo(activeTab, todo.id); }}
                            className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Action row: Transfer button - compact */}
                      {!todo.done && (
                        <div className="mt-1 pt-1 border-t border-slate-200/50">
                          <button
                            onClick={(e) => { e.stopPropagation(); openTransferDialog(activeTab, todo); }}
                            className="ml-9 px-2 py-0.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded flex items-center gap-1 w-fit"
                          >
                            <ChevronRight className="w-3 h-3" />
                            Übertragen
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Quick add inline for Heute - only when tasks exist */}
                <div className="flex gap-1.5 mt-3">
                  <input
                    type="text"
                    enterKeyHint="done"
                    placeholder="+ Aufgabe für Heute..."
                    className="flex-1 p-2 border border-slate-200 rounded-lg text-sm bg-white"
                    data-testid="daily-quick-add"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        const inputText = e.target.value.trim();
                        setDailyTodos(prev => [...prev, {
                          id: Date.now(),
                          text: inputText,
                          priority: 5,
                          done: false,
                          createdAt: new Date().toISOString()
                        }].sort((a, b) => b.priority - a.priority));
                        e.target.value = '';
                      }
                    }}
                  />
                </div>

                {/* Termine section */}
                {todayAppointments.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Termine</p>
                    <div className="space-y-2">
                      {todayAppointments.map(apt => (
                        <div key={apt.id} className={`flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white ${apt.done ? 'opacity-60' : ''}`}>
                          <button
                            onClick={() => onToggleAppointmentDone && onToggleAppointmentDone(apt.id)}
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                              apt.done ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-slate-300 hover:border-emerald-500'
                            }`}
                          >
                            {apt.done && <Check className="w-4 h-4" />}
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${apt.done ? 'line-through text-slate-400' : 'text-slate-700'}`}>{apt.title}</p>
                            <p className="text-xs text-slate-400">{apt.startTime} – {apt.endTime}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()
        )}

        {/* "Alle Übertragen" Button for Daily */}
        {activeTab === 'daily' && dailyTodos.filter(t => !t.done).length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <button
              onClick={() => openBulkTransferModal('daily')}
              className="w-full py-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 bg-purple-50 text-purple-600 hover:bg-purple-100"
            >
              <ChevronRight className="w-4 h-4" />
              Alle Übertragen ({dailyTodos.filter(t => !t.done).length})
            </button>
          </div>
        )}
      </div>

      {/* Bulk Transfer Modal (Heute → Woche) */}
      {showBulkTransferModal && bulkTransferItems.length > 0 && currentBulkIndex < bulkTransferItems.length && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 max-w-md w-full shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Zur Wochenansicht übertragen</h3>
                <p className="text-sm text-slate-500">{currentBulkIndex + 1} von {bulkTransferItems.length}</p>
              </div>
              <button onClick={() => { setShowBulkTransferModal(false); setBulkTransferItems([]); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Progress bar */}
            <div className="h-1 bg-slate-200 rounded-full mb-4">
              <div 
                className="h-1 bg-purple-600 rounded-full transition-all"
                style={{ width: `${((currentBulkIndex + 1) / bulkTransferItems.length) * 100}%` }}
              />
            </div>

            {/* Form */}
            <div className="space-y-4 flex-1 overflow-y-auto">
              <div>
                <label className="text-sm font-medium text-slate-700">Titel</label>
                <input
                  type="text"
                  value={bulkTransferData.title || ''}
                  onChange={(e) => setBulkTransferData({ ...bulkTransferData, title: e.target.value })}
                  className="w-full mt-1 p-3 border border-slate-200 rounded-lg text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Startzeit</label>
                  <input
                    type="time"
                    value={bulkTransferData.startTime || '09:00'}
                    onChange={(e) => {
                      const newStartTime = e.target.value;
                      // For multi-day: no validation, just set the value
                      if (bulkTransferData.isMultiDay) {
                        setBulkTransferData({ ...bulkTransferData, startTime: newStartTime });
                        return;
                      }
                      const currentEndTime = bulkTransferData.endTime || '10:00';
                      const [startH, startM] = newStartTime.split(':').map(Number);
                      const [endH, endM] = currentEndTime.split(':').map(Number);
                      const startMinutes = startH * 60 + startM;
                      const endMinutes = endH * 60 + endM;
                      
                      // If end time is less than 15 min after new start, adjust it
                      if (endMinutes < startMinutes + 15) {
                        const newEndHour = Math.min(startH + 1, 23);
                        const newEndTime = `${newEndHour.toString().padStart(2, '0')}:${startM.toString().padStart(2, '0')}`;
                        setBulkTransferData({ ...bulkTransferData, startTime: newStartTime, endTime: newEndTime });
                      } else {
                        setBulkTransferData({ ...bulkTransferData, startTime: newStartTime });
                      }
                    }}
                    className="w-full mt-1 p-3 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Endzeit</label>
                  <input
                    type="time"
                    value={bulkTransferData.endTime || '10:00'}
                    onChange={(e) => {
                      const newEndTime = e.target.value;
                      // For multi-day: no validation, just set the value
                      if (bulkTransferData.isMultiDay) {
                        setBulkTransferData({ ...bulkTransferData, endTime: newEndTime });
                        return;
                      }
                      const currentStartTime = bulkTransferData.startTime || '09:00';
                      const [startH, startM] = currentStartTime.split(':').map(Number);
                      const [endH, endM] = newEndTime.split(':').map(Number);
                      const startMinutes = startH * 60 + startM;
                      const endMinutes = endH * 60 + endM;
                      
                      // End time must be at least 15 min after start time
                      if (endMinutes >= startMinutes + 15) {
                        setBulkTransferData({ ...bulkTransferData, endTime: newEndTime });
                      } else {
                        // Set to start + 15 min
                        const minEndMinutes = startMinutes + 15;
                        const minEndH = Math.floor(minEndMinutes / 60);
                        const minEndM = minEndMinutes % 60;
                        const minEndTime = `${minEndH.toString().padStart(2, '0')}:${minEndM.toString().padStart(2, '0')}`;
                        setBulkTransferData({ ...bulkTransferData, endTime: minEndTime });
                      }
                    }}
                    className="w-full mt-1 p-3 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Priorität: {bulkTransferData.priority || 5}</label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={bulkTransferData.priority || 5}
                  onChange={(e) => setBulkTransferData({ ...bulkTransferData, priority: parseInt(e.target.value) })}
                  className="w-full mt-1 accent-purple-600"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Notizen</label>
                <textarea
                  value={bulkTransferData.note || ''}
                  onChange={(e) => setBulkTransferData({ ...bulkTransferData, note: e.target.value })}
                  className="w-full mt-1 p-3 border border-slate-200 rounded-lg text-sm resize-none"
                  rows={2}
                />
              </div>

              {/* Mehrtägig Checkbox */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isMultiDay"
                  checked={bulkTransferData.isMultiDay || false}
                  onChange={(e) => {
                    if (e.target.checked) {
                      // Set end date to next day automatically
                      // Use current date as fallback if bulkTransferData.date is not set
                      const baseDate = bulkTransferData.date ? new Date(bulkTransferData.date) : new Date();
                      if (isNaN(baseDate.getTime())) {
                        // If date is invalid, use today
                        const today = new Date();
                        const nextDay = new Date(today);
                        nextDay.setDate(nextDay.getDate() + 1);
                        setBulkTransferData({ 
                          ...bulkTransferData, 
                          isMultiDay: true,
                          date: getLocalDateStr(today),
                          endDate: getLocalDateStr(nextDay)
                        });
                      } else {
                        const nextDay = new Date(baseDate);
                        nextDay.setDate(nextDay.getDate() + 1);
                        setBulkTransferData({ 
                          ...bulkTransferData, 
                          isMultiDay: true,
                          endDate: getLocalDateStr(nextDay)
                        });
                      }
                    } else {
                      setBulkTransferData({ ...bulkTransferData, isMultiDay: false, endDate: null });
                    }
                  }}
                  className="w-4 h-4 accent-purple-600"
                />
                <label htmlFor="isMultiDay" className="text-sm text-slate-700">Mehrtägiger Termin</label>
              </div>

              {/* Bis-Datum wenn mehrtägig */}
              {bulkTransferData.isMultiDay && (
                <div className="grid grid-cols-2 gap-3 pl-6">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Bis Datum</label>
                    <input
                      type="date"
                      value={bulkTransferData.endDate || ''}
                      min={(() => {
                        const baseDate = bulkTransferData.date ? new Date(bulkTransferData.date) : new Date();
                        if (isNaN(baseDate.getTime())) return getLocalDateStr();
                        const nextDay = new Date(baseDate);
                        nextDay.setDate(nextDay.getDate() + 1);
                        return getLocalDateStr(nextDay);
                      })()}
                      onChange={(e) => setBulkTransferData({ ...bulkTransferData, endDate: e.target.value })}
                      className="w-full mt-1 p-3 border border-slate-200 rounded-lg text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex gap-2 pt-4 mt-4 border-t">
              <Button onClick={saveBulkTransferItem} className="flex-1 bg-purple-600 hover:bg-purple-700">
                Speichern
              </Button>
              <Button onClick={skipBulkTransferItem} variant="outline">
                Überspringen
              </Button>
              <Button onClick={() => { setShowBulkTransferModal(false); setBulkTransferItems([]); }} variant="outline" className="text-slate-500">
                Abbrechen
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Todo Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 max-w-md w-full shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">Neue Aufgabe</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Aufgabe</label>
                <input
                  type="text"
                  value={newTodo.text}
                  onChange={(e) => setNewTodo({ ...newTodo, text: e.target.value })}
                  placeholder="Was möchtest du erledigen?"
                  className="w-full mt-1 p-3 border border-slate-200 rounded-lg text-sm"
                  autoFocus
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Priorität: {newTodo.priority}</label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={newTodo.priority}
                  onChange={(e) => setNewTodo({ ...newTodo, priority: parseInt(e.target.value) })}
                  className="w-full mt-1 accent-emerald-600"
                />
                <div className="flex justify-between text-xs text-slate-500">
                  <span>1 (niedrig)</span>
                  <span>10 (hoch)</span>
                </div>
              </div>

              {/* Due Date - für alle Tabs - nur Zukunft */}
              <div>
                <label className="text-sm font-medium text-slate-700">Fällig bis (optional)</label>
                <input
                  type="date"
                  value={newTodo.dueDate}
                  min={getLocalDateStr()}
                  onChange={(e) => setNewTodo({ ...newTodo, dueDate: e.target.value })}
                  className="w-full mt-1 p-3 border border-slate-200 rounded-lg text-sm"
                />
              </div>

              {/* Wochentag - nur für Woche Tab */}
              {activeTab === 'weekly' && (
                <div>
                  <label className="text-sm font-medium text-slate-700">Wochentag</label>
                  <select
                    value={newTodo.weekday ?? ''}
                    onChange={(e) => setNewTodo({ ...newTodo, weekday: e.target.value === '' ? undefined : parseInt(e.target.value) })}
                    className="w-full mt-1 p-3 border border-slate-200 rounded-lg text-sm bg-white"
                  >
                    <option value="">Diese Woche (flexibel)</option>
                    {(() => {
                      const now = new Date();
                      const todayWeekday = now.getDay() === 0 ? 6 : now.getDay() - 1; // 0=Mo, 6=So
                      const weekdays = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
                      // Show today and future days
                      return weekdays.map((day, idx) => (
                        idx >= todayWeekday ? (
                          <option key={idx} value={idx}>
                            {day}{idx === todayWeekday ? ' - Heute' : ''}
                          </option>
                        ) : null
                      )).filter(Boolean);
                    })()}
                  </select>
                  <p className="text-xs text-slate-400 mt-1">Heute → wechselt automatisch zu Heute-Tab</p>
                </div>
              )}

              {/* KW - nur für Monat Tab */}
              {activeTab === 'monthly' && (
                <div>
                  <label className="text-sm font-medium text-slate-700">Kalenderwoche</label>
                  <select
                    value={newTodo.weekNum ?? ''}
                    onChange={(e) => setNewTodo({ ...newTodo, weekNum: e.target.value === '' ? undefined : parseInt(e.target.value) })}
                    className="w-full mt-1 p-3 border border-slate-200 rounded-lg text-sm bg-white"
                  >
                    <option value="">Diesen Monat (flexibel)</option>
                    {(() => {
                      const weeks = getWeeksInMonthForTodos();
                      const currentWeek = getWeekNumber(new Date());
                      // Only show current and future weeks
                      return weeks.filter(w => w.weekNum >= currentWeek).map(week => (
                        <option key={week.weekNum} value={week.weekNum}>
                          KW {week.weekNum} ({week.dateRange}){week.isCurrent ? ' - Aktuell' : ''}
                        </option>
                      ));
                    })()}
                  </select>
                  <p className="text-xs text-slate-400 mt-1">Aktuelle KW → wechselt automatisch zu Woche-Tab</p>
                </div>
              )}

              {/* Monat - nur für Dieses Jahr Tab */}
              {activeTab === 'open' && (
                <div>
                  <label className="text-sm font-medium text-slate-700">Monat</label>
                  <select
                    value={newTodo.monthIndex ?? ''}
                    onChange={(e) => setNewTodo({ ...newTodo, monthIndex: e.target.value === '' ? undefined : parseInt(e.target.value) })}
                    className="w-full mt-1 p-3 border border-slate-200 rounded-lg text-sm bg-white"
                  >
                    <option value="">Dieses Jahr (flexibel)</option>
                    {(() => {
                      const now = new Date();
                      const currentMonth = now.getMonth();
                      const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
                      return monthNames.map((name, idx) => (
                        idx >= currentMonth ? (
                          <option key={idx} value={idx}>
                            {name}{idx === currentMonth ? ' - Aktuell' : ''}
                          </option>
                        ) : null
                      )).filter(Boolean);
                    })()}
                  </select>
                  <p className="text-xs text-slate-400 mt-1">Aktueller Monat → wechselt automatisch zu Monat-Tab</p>
                </div>
              )}

              {/* Notizen */}
              <div>
                <label className="text-sm font-medium text-slate-700">Notizen (optional)</label>
                <textarea
                  value={newTodo.note}
                  onChange={(e) => setNewTodo({ ...newTodo, note: e.target.value })}
                  placeholder="Zusätzliche Infos..."
                  className="w-full mt-1 p-3 border border-slate-200 rounded-lg text-sm resize-none"
                  rows={2}
                />
              </div>

              {/* Kontakt verknüpfen */}
              <div className="relative" ref={addContactSearchRef}>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Kontakt verknüpfen (optional)</label>
                {newTodo.contactRef?.name ? (
                  <div className="flex items-center gap-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <span className="text-sm text-emerald-700 font-medium flex-1">{newTodo.contactRef.name}</span>
                    <button type="button" onClick={() => { setNewTodo({ ...newTodo, contactRef: undefined }); setAddContactSearch(''); }}>
                      <svg className="w-3.5 h-3.5 text-slate-400 hover:text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="text"
                      value={addContactSearch}
                      onChange={(e) => { setAddContactSearch(e.target.value); setAddContactSearchOpen(true); }}
                      onFocus={() => setAddContactSearchOpen(true)}
                      placeholder="Kontaktname eingeben..."
                      className="w-full p-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 pl-8"
                    />
                    <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                    {addContactSearchOpen && addContactResults.length > 0 && (
                      <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-36 overflow-y-auto">
                        {addContactResults.map(c => (
                          <button type="button" key={c.id}
                            onClick={() => { setNewTodo({ ...newTodo, contactRef: { id: c.id, typ: c.typ || 'teampartner', name: c.name } }); setAddContactSearch(c.name); setAddContactSearchOpen(false); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 flex items-center gap-2"
                          >
                            <span>{c.name}</span>
                            {c.typ && <span className="text-xs text-slate-400 ml-auto">{c.typ}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={handleAddTodo} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  Hinzufügen
                </Button>
                <Button onClick={() => setShowAddModal(false)} variant="outline">
                  Abbrechen
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Modal - Choose Target */}
      {showTransferModal && todoToTransfer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 max-w-sm w-full shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">
                {transferStage === 'detail'
                  ? (pendingTransferTarget === 'weekly' ? 'Tag wählen' :
                     pendingTransferTarget === 'monthly' ? 'KW wählen' :
                     pendingTransferTarget === 'open' ? 'Monat wählen' : 'Übertragen nach...')
                  : 'Übertragen nach...'}
              </h3>
              <button onClick={() => { setShowTransferModal(false); setTransferStage('main'); setPendingTransferTarget(null); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-slate-600 mb-4 truncate">"{todoToTransfer.text}"</p>

            {/* Contact sync warning */}
            {todoToTransfer.contactRef?.name && transferStage === 'main' && (
              <div className="mb-3 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700 flex items-center gap-1.5">
                <span>👤</span>
                <span>Verknüpft mit <strong>{todoToTransfer.contactRef.name}</strong> – Sync bleibt erhalten</span>
              </div>
            )}

            {/* ─── STAGE: DETAIL ─── */}
            {transferStage === 'detail' && (() => {
              const now = new Date();
              const todayWd = now.getDay() === 0 ? 6 : now.getDay() - 1;
              const currentKW = getWeekNumber(now);
              const currentMonth = now.getMonth();
              const currentYear = now.getFullYear();

              if (pendingTransferTarget === 'weekly') {
                // Verbleibende Wochentage
                return (
                  <div className="space-y-3">
                    <p className="text-xs text-slate-500 font-medium">An welchen Tag?</p>
                    <button
                      onClick={() => executeTransfer('weekly')}
                      data-testid="transfer-detail-weekly-general"
                      className="w-full py-3 px-4 rounded-lg bg-purple-50 text-purple-700 font-semibold hover:bg-purple-100 flex items-center justify-between"
                    >
                      <span>Diese Woche (unzugewiesen)</span>
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    <div className="grid grid-cols-4 gap-1.5">
                      {WEEKDAY_NAMES.map((name, idx) => idx <= todayWd ? null : (
                        <button
                          key={idx}
                          onClick={() => executeTransfer('weekly', { weekday: idx })}
                          data-testid={`transfer-detail-weekday-${idx}`}
                          className="py-2 px-1 rounded-lg text-xs font-medium transition-colors bg-slate-50 text-slate-600 hover:bg-emerald-100 hover:text-emerald-700"
                        >
                          {name}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => { setTransferStage('main'); setPendingTransferTarget(null); }}
                      className="w-full py-2 text-xs text-slate-500 hover:text-slate-700"
                    >
                      ← Zurück
                    </button>
                  </div>
                );
              }

              if (pendingTransferTarget === 'monthly') {
                // Verbleibende KWs des aktuellen Monats
                const lastDay = new Date(currentYear, currentMonth + 1, 0);
                const kwsInMonth = new Set();
                for (let d = new Date(currentYear, currentMonth, 1); d <= lastDay; d.setDate(d.getDate() + 1)) {
                  kwsInMonth.add(getWeekNumber(d));
                }
                const futureKWs = Array.from(kwsInMonth).sort((a, b) => a - b).filter(kw => kw > currentKW);
                return (
                  <div className="space-y-3">
                    <p className="text-xs text-slate-500 font-medium">Welche KW?</p>
                    <button
                      onClick={() => executeTransfer('monthly')}
                      data-testid="transfer-detail-monthly-general"
                      className="w-full py-3 px-4 rounded-lg bg-blue-50 text-blue-700 font-semibold hover:bg-blue-100 flex items-center justify-between"
                    >
                      <span>Diesen Monat (unzugewiesen)</span>
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    {futureKWs.length > 0 ? (
                      <div className="grid grid-cols-4 gap-1.5">
                        {futureKWs.map(kw => (
                          <button
                            key={kw}
                            onClick={() => executeTransfer('monthly', { weekNum: kw })}
                            data-testid={`transfer-detail-kw-${kw}`}
                            className="py-2 px-1 rounded-lg text-xs font-medium transition-colors bg-slate-50 text-slate-600 hover:bg-emerald-100 hover:text-emerald-700"
                          >
                            KW {kw}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic text-center">Keine weiteren Wochen in diesem Monat</p>
                    )}
                    <button
                      onClick={() => { setTransferStage('main'); setPendingTransferTarget(null); }}
                      className="w-full py-2 text-xs text-slate-500 hover:text-slate-700"
                    >
                      ← Zurück
                    </button>
                  </div>
                );
              }

              if (pendingTransferTarget === 'open') {
                // Verbleibende Monate des aktuellen Jahres
                const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
                const futureMonths = MONTH_NAMES_SHORT.map((name, idx) => ({ name, idx })).filter(m => m.idx > currentMonth);
                return (
                  <div className="space-y-3">
                    <p className="text-xs text-slate-500 font-medium">Welcher Monat?</p>
                    <button
                      onClick={() => executeTransfer('open')}
                      data-testid="transfer-detail-year-general"
                      className="w-full py-3 px-4 rounded-lg bg-indigo-50 text-indigo-700 font-semibold hover:bg-indigo-100 flex items-center justify-between"
                    >
                      <span>Dieses Jahr (unzugewiesen)</span>
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    {futureMonths.length > 0 ? (
                      <div className="grid grid-cols-4 gap-1.5">
                        {futureMonths.map(m => (
                          <button
                            key={m.idx}
                            onClick={() => executeTransfer('open', { monthIndex: m.idx })}
                            data-testid={`transfer-detail-month-${m.idx}`}
                            className="py-2 px-1 rounded-lg text-xs font-medium transition-colors bg-slate-50 text-slate-600 hover:bg-emerald-100 hover:text-emerald-700"
                          >
                            {m.name}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic text-center">Keine weiteren Monate in diesem Jahr</p>
                    )}
                    <button
                      onClick={() => { setTransferStage('main'); setPendingTransferTarget(null); }}
                      className="w-full py-2 text-xs text-slate-500 hover:text-slate-700"
                    >
                      ← Zurück
                    </button>
                  </div>
                );
              }
              return null;
            })()}

            {/* ─── STAGE: MAIN (Ziel-Tab-Auswahl) ─── */}
            {transferStage === 'main' && (
              <>
            <p className="text-xs text-slate-500 mb-2 font-medium">Übertragen zu:</p>
            <div className="space-y-2">
              {/* Bei daily: Kalender oben */}
              {todoToTransfer.fromTab === 'daily' && (
                <>
                  <button
                    onClick={openCalendarTransfer}
                    className="w-full py-3 px-4 rounded-lg bg-orange-50 text-orange-700 font-medium hover:bg-orange-100 flex items-center justify-between"
                  >
                    <span>📅 Zum Kalender</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => startTransferWithDetail('weekly')}
                    className="w-full py-3 px-4 rounded-lg bg-purple-50 text-purple-700 font-medium hover:bg-purple-100 flex items-center justify-between"
                  >
                    <span>zurück zu Wochen To-dos</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}

              {/* weekly → daily (1 Schritt ab) oder → monthly (1 Schritt auf) */}
              {todoToTransfer.fromTab === 'weekly' && (
                <>
                  <button
                    onClick={() => startTransferWithDetail('monthly')}
                    className="w-full py-3 px-4 rounded-lg bg-blue-50 text-blue-700 font-medium hover:bg-blue-100 flex items-center justify-between"
                  >
                    <span>Monat</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => executeTransfer('daily')}
                    className="w-full py-3 px-4 rounded-lg bg-emerald-50 text-emerald-700 font-medium hover:bg-emerald-100 flex items-center justify-between"
                  >
                    <span>Heute</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}

              {/* monthly → open/year (1 auf) + abwärts alle: weekly, daily */}
              {todoToTransfer.fromTab === 'monthly' && (
                <>
                  <button
                    onClick={() => startTransferWithDetail('open')}
                    className="w-full py-3 px-4 rounded-lg bg-indigo-50 text-indigo-700 font-medium hover:bg-indigo-100 flex items-center justify-between"
                  >
                    <span>Dieses Jahr</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => startTransferWithDetail('weekly')}
                    className="w-full py-3 px-4 rounded-lg bg-purple-50 text-purple-700 font-medium hover:bg-purple-100 flex items-center justify-between"
                  >
                    <span>Woche</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => executeTransfer('daily')}
                    className="w-full py-3 px-4 rounded-lg bg-emerald-50 text-emerald-700 font-medium hover:bg-emerald-100 flex items-center justify-between"
                  >
                    <span>Heute</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}

              {/* open/year → abwärts alle: monthly, weekly, daily */}
              {todoToTransfer.fromTab === 'open' && (
                <>
                  <button
                    onClick={() => startTransferWithDetail('monthly')}
                    className="w-full py-3 px-4 rounded-lg bg-blue-50 text-blue-700 font-medium hover:bg-blue-100 flex items-center justify-between"
                  >
                    <span>Monat</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => startTransferWithDetail('weekly')}
                    className="w-full py-3 px-4 rounded-lg bg-purple-50 text-purple-700 font-medium hover:bg-purple-100 flex items-center justify-between"
                  >
                    <span>Woche</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => executeTransfer('daily')}
                    className="w-full py-3 px-4 rounded-lg bg-emerald-50 text-emerald-700 font-medium hover:bg-emerald-100 flex items-center justify-between"
                  >
                    <span>Heute</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}

              {/* Tag wechseln - nur für weekly (direkte Zuweisung ohne Transfer) */}
              {todoToTransfer.fromTab === 'weekly' && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-xs text-slate-500 mb-2 font-medium">Tag wechseln:</p>
                      <div className="grid grid-cols-4 gap-1.5">
                        <button
                          onClick={() => changeTodoWeekday(undefined)}
                          className={`py-2 px-1 rounded-lg text-xs font-medium transition-colors ${
                            todoToTransfer.weekday === undefined
                              ? 'bg-purple-500 text-white'
                              : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                          }`}
                        >
                          Diese Woche
                        </button>
                        {(() => {
                          const d = new Date();
                          const todayWd = d.getDay() === 0 ? 6 : d.getDay() - 1;
                          return WEEKDAY_NAMES.map((name, idx) => idx <= todayWd ? null : (
                            <button
                              key={idx}
                              onClick={() => changeTodoWeekday(idx)}
                              className={`py-2 px-1 rounded-lg text-xs font-medium transition-colors ${
                                todoToTransfer.weekday === idx
                                  ? 'bg-emerald-500 text-white'
                                  : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                              }`}
                            >
                              {name}
                            </button>
                          ));
                        })()}
                      </div>
                    </div>
                  )}

                  {/* KW wechseln - nur für monthly */}
                  {todoToTransfer.fromTab === 'monthly' && (() => {
                    const now = new Date();
                    const currentKW = getWeekNumber(now);
                    const currentYear = now.getFullYear();
                    const currentMonth = now.getMonth();
                    // Alle KWs des aktuellen Monats ermitteln
                    const lastDay = new Date(currentYear, currentMonth + 1, 0);
                    const kwsInMonth = new Set();
                    for (let d = new Date(currentYear, currentMonth, 1); d <= lastDay; d.setDate(d.getDate() + 1)) {
                      kwsInMonth.add(getWeekNumber(d));
                    }
                    const futureKWs = Array.from(kwsInMonth).sort((a, b) => a - b).filter(kw => kw > currentKW);
                    return (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <p className="text-xs text-slate-500 mb-2 font-medium">KW wechseln:</p>
                        <div className="grid grid-cols-4 gap-1.5">
                          <button
                            onClick={() => changeTodoKW(undefined)}
                            className={`py-2 px-1 rounded-lg text-xs font-medium transition-colors ${
                              todoToTransfer.weekNum === undefined
                                ? 'bg-purple-500 text-white'
                                : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                            }`}
                          >
                            Diesen Monat
                          </button>
                          {futureKWs.map(kw => (
                            <button
                              key={kw}
                              onClick={() => changeTodoKW(kw)}
                              className={`py-2 px-1 rounded-lg text-xs font-medium transition-colors ${
                                todoToTransfer.weekNum === kw
                                  ? 'bg-emerald-500 text-white'
                                  : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                              }`}
                            >
                              KW {kw}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Monat wechseln - nur für open/Jahr */}
                  {todoToTransfer.fromTab === 'open' && (() => {
                    const now = new Date();
                    const currentMonth = now.getMonth();
                    const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
                    return (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <p className="text-xs text-slate-500 mb-2 font-medium">Monat wechseln:</p>
                        <div className="grid grid-cols-4 gap-1.5">
                          <button
                            onClick={() => changeTodoMonth(undefined)}
                            className={`py-2 px-1 rounded-lg text-xs font-medium transition-colors ${
                              todoToTransfer.monthIndex === undefined
                                ? 'bg-purple-500 text-white'
                                : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                            }`}
                          >
                            Dieses Jahr
                          </button>
                          {MONTH_NAMES_SHORT.map((name, idx) => idx <= currentMonth ? null : (
                            <button
                              key={idx}
                              onClick={() => changeTodoMonth(idx)}
                              className={`py-2 px-1 rounded-lg text-xs font-medium transition-colors ${
                                todoToTransfer.monthIndex === idx
                                  ? 'bg-emerald-500 text-white'
                                  : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                              }`}
                            >
                              {name}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Zum Kalender - unten für alle außer daily (daily hat ihn oben) */}
                  {todoToTransfer.fromTab !== 'daily' && (
                    <>
                      <p className="text-xs text-slate-500 mb-2 mt-4 font-medium">Zum Kalender:</p>
                      <button
                        onClick={openCalendarTransfer}
                        className="w-full py-3 px-4 rounded-lg bg-orange-50 text-orange-700 font-medium hover:bg-orange-100 flex items-center justify-between"
                      >
                        <span>📅 Zum Kalender übertragen</span>
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </>
                  )}
            </div>
              </>
            )}

            <button
              onClick={() => { setShowTransferModal(false); setTransferStage('main'); setPendingTransferTarget(null); }}
              className="w-full mt-4 py-2 text-slate-500 text-sm hover:text-slate-700"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Calendar Selection Modal */}
      {showCalendarModal && todoToCalendar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 max-w-sm w-full shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">Kalender wählen</h3>
              <button onClick={() => { setShowCalendarModal(false); setTodoToCalendar(null); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-slate-600 mb-4 truncate">"{todoToCalendar.text}"</p>

            <div className="space-y-2">
              <button
                onClick={() => transferToCalendar('week')}
                className="w-full py-3 px-4 rounded-lg bg-purple-50 text-purple-700 font-medium hover:bg-purple-100 flex items-center justify-between"
              >
                <span>Wochenansicht</span>
                <ChevronRight className="w-5 h-5" />
              </button>
              
              <button
                onClick={() => transferToCalendar('month')}
                className="w-full py-3 px-4 rounded-lg bg-blue-50 text-blue-700 font-medium hover:bg-blue-100 flex items-center justify-between"
              >
                <span>Monatsansicht</span>
                <ChevronRight className="w-5 h-5" />
              </button>
              
              {/* Jahresansicht nur bei "offen" Tab */}
              {todoToCalendar.fromTab === 'open' && (
                <button
                  onClick={() => transferToCalendar('year')}
                  className="w-full py-3 px-4 rounded-lg bg-emerald-50 text-emerald-700 font-medium hover:bg-emerald-100 flex items-center justify-between"
                >
                  <span>Jahresansicht</span>
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
            </div>

            <button
              onClick={() => { setShowCalendarModal(false); setTodoToCalendar(null); }}
              className="w-full mt-4 py-2 text-slate-500 text-sm hover:text-slate-700"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Edit Todo Modal */}
      {showEditModal && todoToEdit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 max-w-md w-full shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">Aufgabe bearbeiten</h3>
              <button onClick={() => { setShowEditModal(false); setTodoToEdit(null); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Aufgabe</label>
                <input
                  type="text"
                  value={todoToEdit.text}
                  onChange={(e) => setTodoToEdit({ ...todoToEdit, text: e.target.value })}
                  className="w-full mt-1 p-3 border border-slate-200 rounded-lg text-sm"
                  autoFocus
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Priorität: {todoToEdit.priority}</label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={todoToEdit.priority}
                  onChange={(e) => setTodoToEdit({ ...todoToEdit, priority: parseInt(e.target.value) })}
                  className="w-full mt-1 accent-emerald-600"
                />
                <div className="flex justify-between text-xs text-slate-500">
                  <span>1 (niedrig)</span>
                  <span>10 (hoch)</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Fällig bis (optional)</label>
                <input
                  type="date"
                  value={todoToEdit.dueDate || ''}
                  min={getLocalDateStr()}
                  onChange={(e) => setTodoToEdit({ ...todoToEdit, dueDate: e.target.value })}
                  className="w-full mt-1 p-3 border border-slate-200 rounded-lg text-sm"
                />
              </div>

              {activeTab === 'weekly' && (
                <div>
                  <label className="text-sm font-medium text-slate-700">Wochentag</label>
                  <select
                    value={todoToEdit.weekday ?? ''}
                    onChange={(e) => setTodoToEdit({ ...todoToEdit, weekday: e.target.value === '' ? undefined : parseInt(e.target.value) })}
                    className="w-full mt-1 p-3 border border-slate-200 rounded-lg text-sm bg-white"
                  >
                    <option value="">Diese Woche (flexibel)</option>
                    {(() => {
                      const now = new Date();
                      const todayWeekday = now.getDay() === 0 ? 6 : now.getDay() - 1;
                      const weekdays = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
                      return weekdays.map((day, idx) => (
                        idx >= todayWeekday ? (
                          <option key={idx} value={idx}>
                            {day}{idx === todayWeekday ? ' - Heute' : ''}
                          </option>
                        ) : null
                      )).filter(Boolean);
                    })()}
                  </select>
                </div>
              )}

              {activeTab === 'monthly' && (
                <div>
                  <label className="text-sm font-medium text-slate-700">Kalenderwoche</label>
                  <select
                    value={todoToEdit.weekNum ?? ''}
                    onChange={(e) => setTodoToEdit({ ...todoToEdit, weekNum: e.target.value === '' ? undefined : parseInt(e.target.value) })}
                    className="w-full mt-1 p-3 border border-slate-200 rounded-lg text-sm bg-white"
                  >
                    <option value="">Diesen Monat (flexibel)</option>
                    {(() => {
                      const weeks = getWeeksInMonthForTodos();
                      const currentWeek = getWeekNumber(new Date());
                      return weeks.filter(w => w.weekNum >= currentWeek).map(week => (
                        <option key={week.weekNum} value={week.weekNum}>
                          KW {week.weekNum} ({week.dateRange}){week.isCurrent ? ' - Aktuell' : ''}
                        </option>
                      ));
                    })()}
                  </select>
                </div>
              )}

              {activeTab === 'open' && (
                <div>
                  <label className="text-sm font-medium text-slate-700">Monat</label>
                  <select
                    value={todoToEdit.monthIndex ?? ''}
                    onChange={(e) => setTodoToEdit({ ...todoToEdit, monthIndex: e.target.value === '' ? undefined : parseInt(e.target.value) })}
                    className="w-full mt-1 p-3 border border-slate-200 rounded-lg text-sm bg-white"
                  >
                    <option value="">Dieses Jahr/Backlog (flexibel)</option>
                    {(() => {
                      const now = new Date();
                      const currentMonth = now.getMonth();
                      const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
                      return monthNames.map((name, idx) => (
                        idx >= currentMonth ? (
                          <option key={idx} value={idx}>
                            {name}{idx === currentMonth ? ' - Aktuell' : ''}
                          </option>
                        ) : null
                      )).filter(Boolean);
                    })()}
                  </select>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-slate-700">Notizen (optional)</label>
                <textarea
                  value={todoToEdit.note || ''}
                  onChange={(e) => setTodoToEdit({ ...todoToEdit, note: e.target.value })}
                  placeholder="Zusätzliche Infos..."
                  className="w-full mt-1 p-3 border border-slate-200 rounded-lg text-sm resize-none"
                  rows={2}
                />
              </div>

              {/* Contact link */}
              <div className="relative" ref={contactSearchRef}>
                <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5" />
                  Kontakt verknüpfen (optional)
                </label>
                {todoToEdit.contactRef?.name ? (
                  <div className="mt-1 flex items-center gap-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <span className="text-sm text-emerald-700 font-medium flex-1">{todoToEdit.contactRef.name}</span>
                    <button
                      onClick={() => { setTodoToEdit({ ...todoToEdit, contactRef: undefined }); setContactSearch(''); }}
                      className="text-slate-400 hover:text-red-500"
                      data-testid="remove-contact-link"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="relative mt-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <input
                      type="text"
                      data-testid="contact-search-input"
                      value={contactSearch}
                      onChange={(e) => { setContactSearch(e.target.value); setContactSearchOpen(true); }}
                      onFocus={() => setContactSearchOpen(true)}
                      placeholder="Name eingeben..."
                      className="w-full pl-8 pr-3 p-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    />
                    {contactSearchOpen && contactResults.length > 0 && (
                      <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                        {contactResults.map(c => (
                          <button
                            key={c.id}
                            data-testid={`contact-result-${c.id}`}
                            onClick={() => {
                              setTodoToEdit({ ...todoToEdit, contactRef: { id: c.id, typ: c.typ || 'teampartner', name: c.name } });
                              setContactSearch(c.name);
                              setContactSearchOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 flex items-center gap-2"
                          >
                            <span className="text-base">{c.typ === 'teampartner' ? '👤' : c.typ === 'kunde' ? '🛒' : '❄️'}</span>
                            <span>{c.name}</span>
                            {c.typ && <span className="text-xs text-slate-400 ml-auto capitalize">{c.typ}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={saveEditedTodo} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  Speichern
                </Button>
                <Button onClick={deleteFromEditModal} variant="outline" className="text-red-600 border-red-300 hover:bg-red-50">
                  Löschen
                </Button>
                <Button onClick={() => { setShowEditModal(false); setTodoToEdit(null); }} variant="outline">
                  Abbrechen
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

